import { useCallback, useEffect, useRef } from "react";
import { useRequest } from "hooks/useRequest";
import api from "utils/api";
import type {
  UseSeenDiagramOccurrencesArgs,
  UseSeenDiagramOccurrencesResult,
} from "./types";

const EMPTY_SET = new Set<string>();

export const useSeenDiagramOccurrences = ({
  itemTypeId,
}: UseSeenDiagramOccurrencesArgs): UseSeenDiagramOccurrencesResult => {
  const cacheRef = useRef<Map<number, Set<string>>>(new Map());

  const fetchSeenIds = useCallback(async (id: number): Promise<Set<string>> => {
    const cached = cacheRef.current.get(id);
    if (cached) return cached;

    const response: { occurrenceIds: number[] } =
      await api.getSeenDiagramOccurrenceIds(id);
    const ids = new Set(response.occurrenceIds.map(String));
    cacheRef.current.set(id, ids);
    return ids;
  }, []);

  const { data, isLoading, run, mutateData } = useRequest(fetchSeenIds, {
    manual: true,
  });

  const seenIds = itemTypeId ? (data ?? EMPTY_SET) : EMPTY_SET;

  const markSeen = useCallback(
    (occurrenceId: string) => {
      mutateData({
        updater: (prev) => {
          if (prev.has(occurrenceId)) return prev;
          const next = new Set(prev);
          next.add(occurrenceId);

          if (itemTypeId) {
            cacheRef.current.set(itemTypeId, next);
          }

          return next;
        },
      });

      const numericId = parseInt(occurrenceId, 10);
      if (!isNaN(numericId)) {
        api.markDiagramOccurrenceSeen(numericId).catch(() => {
          // Best-effort — marking seen is not critical
        });
      }
    },
    [itemTypeId, mutateData],
  );

  useEffect(() => {
    if (!itemTypeId) return;
    run(itemTypeId);
  }, [itemTypeId, run]);

  return { seenIds, isLoading, markSeen };
};
